import classNames from "classnames";
import { ButtonHTMLAttributes, ReactNode, Ref } from "react";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { markEventBlocked } from "@design-system/ViewTable/ViewTable.lib";
import { MatchCountChip } from "@components/accounting/transactionMatch/MatchCountChip";
import {
    buildScheduleCardCopy,
    formatScheduleCardText,
    ScheduleCardCopy,
    ScheduleCardValues,
} from "./scheduleCardCopy";
import styles from "./ScheduleCard.module.scss";

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
    schedule: ScheduleCardValues;
    ref?: Ref<HTMLButtonElement>;
    /** Shown in place of the name, e.g. a count of several schedules. */
    leading?: ReactNode;
    /** Replaces the one-line reading. */
    tooltipContent?: ReactNode;
    tooltipClassName?: string;
}

/**
 * The Schedule column's applied state in one pill (Figma 9851:6636): name,
 * then the amount left. A line-scoped schedule shows "Via N line items" in
 * place of the name, like the matched card. The policy name, end date and
 * Paused/Stopped/Completed label live only in the tooltip's full line, which
 * opens on every hover. The name, or the chip inside the Via segment, gives
 * way when the cell is narrow; the amount keeps its width. Renders a button
 * so a picker can use it as its trigger.
 *
 * The tooltip trigger is a span around the button, so hovering anywhere on the
 * card shows it and the picker trigger's own props on the button stay
 * untouched.
 */
/** "Via" and a count chip, standing in for the name on both cards. */
export function ScheduleCardVia({ label }: { label: string }) {
    return (
        <span className={styles.via}>
            Via <MatchCountChip label={label} />
        </span>
    );
}

function resolveNameNode({
    leading,
    copy,
}: {
    leading: ReactNode | undefined;
    copy: ScheduleCardCopy;
}): ReactNode | null {
    if (leading !== undefined) {
        return leading;
    }
    if (copy.lineItemCountLabel !== null) {
        return <ScheduleCardVia label={copy.lineItemCountLabel} />;
    }
    return copy.name.trim() !== "" ? copy.name : null;
}

export function ScheduleCard({
    schedule,
    className,
    ref,
    leading,
    tooltipContent,
    tooltipClassName,
    ...buttonProps
}: Props) {
    const copy = buildScheduleCardCopy(schedule);
    const nameNode = resolveNameNode({ leading, copy });

    return (
        <Tooltip
            content={tooltipContent ?? formatScheduleCardText(copy)}
            className={tooltipClassName}
        >
            <span className={styles.wrapper}>
                <button
                    type="button"
                    {...buttonProps}
                    ref={ref}
                    className={classNames(styles.card, className)}
                    onClick={(event) => {
                        markEventBlocked(event);
                        buttonProps.onClick?.(event);
                    }}
                >
                    {nameNode !== null && (
                        <span className={styles.name}>{nameNode}</span>
                    )}
                    <span className={styles.amount}>{copy.amountLabel}</span>
                </button>
            </span>
        </Tooltip>
    );
}
